import { Formio } from '@evi-ui/js';

interface LocalPreview {
  objectUrl: string;
  autoDownloadPending: boolean;
}

/**
 * Wraps @evi-ui/js's 'url' storage provider so a File component with "Display as image(s)"
 * shows the image the user just picked, and the document returned by the upload API is
 * downloaded once right after upload.
 *
 * The File component previews an image by calling downloadFile(file) with no options and
 * using the resolved { url }. Some upload endpoints don't return the uploaded image (e.g.
 * bail-order-line-items/upload-signature returns the stamped letter PDF), so the backend
 * url can't always be shown. uploadFile() still receives the original File, so we keep an
 * object URL for it, keyed by the returned fileDownloadUri, and serve that as the preview.
 *
 * To skip the auto-download for a field, set its "Custom request options" to
 * { "disableAutoDownload": true }. The flag is stripped before the upload request.
 */
export class FormioUrlStorageProvider {
  private static readonly localPreviews = new Map<string, LocalPreview>();

  static register() {
    const providers = (Formio as any).Providers;
    const baseProvider = providers.getProvider('storage', 'url');
    if (!baseProvider || baseProvider.localPreviewWrapped) {
      return;
    }

    const localPreviews = FormioUrlStorageProvider.localPreviews;
    const wrappedProvider: any = (formio: any) => {
      const base = baseProvider(formio);
      return {
        ...base,
        uploadFile(
          file: File,
          name: string,
          dir: string,
          progressCallback: any,
          url: string,
          options: any,
          ...rest: any[]
        ) {
          const { disableAutoDownload, requestOptions } = FormioUrlStorageProvider.splitOptions(options);
          return base
            .uploadFile(file, name, dir, progressCallback, url, requestOptions, ...rest)
            .then((fileInfo: any) => {
              const downloadUri = fileInfo?.data?.fileDownloadUri;
              if (downloadUri && file?.type?.startsWith('image/')) {
                localPreviews.set(downloadUri, {
                  objectUrl: URL.createObjectURL(file),
                  autoDownloadPending: !disableAutoDownload
                });
              }
              return fileInfo;
            });
        },
        downloadFile(file: any, options?: any) {
          // getFile() (explicit download link) always passes options
          if (typeof options !== 'undefined') {
            return base.downloadFile(file, options);
          }

          const downloadUri = file?.data?.fileDownloadUri;
          const preview = downloadUri ? localPreviews.get(downloadUri) : undefined;
          if (preview) {
            if (preview.autoDownloadPending) {
              preview.autoDownloadPending = false;
              base.downloadFile(file, {});
            }
            return Promise.resolve({ url: preview.objectUrl, storage: 'url' });
          }

          // Saved file with no local copy (form reopened): fall back to the backend url
          const baseFileDownloadUrl = localStorage.getItem('basePathForFileProvider') || '';
          return Promise.resolve({ url: baseFileDownloadUrl + (downloadUri || ''), storage: 'url' });
        }
      };
    };
    wrappedProvider.title = baseProvider.title;
    wrappedProvider.localPreviewWrapped = true;
    providers.addProvider('storage', 'url', wrappedProvider);
  }

  // The url provider copies every request option onto the XHR, so our flag is removed here
  private static splitOptions(options: any): { disableAutoDownload: boolean; requestOptions: any } {
    if (!options) {
      return { disableAutoDownload: false, requestOptions: options };
    }
    let parsed: any;
    try {
      parsed = typeof options === 'string' ? JSON.parse(options) : options;
    } catch (e) {
      return { disableAutoDownload: false, requestOptions: options };
    }
    const { disableAutoDownload, ...requestOptions } = parsed || {};
    return {
      disableAutoDownload: disableAutoDownload === true,
      requestOptions: Object.keys(requestOptions).length ? requestOptions : undefined
    };
  }
}
